import { fetchUtils, withLifecycleCallbacks, combineDataProviders } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import { BASE_URL, API_ENTRYPOINT, API_V2_ENTRYPOINT, GERO_API_BASE_URL, getAuthHeaders } from '../../config/api';
import { handleImageBase64Conversion } from '../../config/Tools';
import { buildNewsPostPayload, toNewsPostValidationError, withNewsPostImage } from '../../config/Tools/newsPosts';
import { NEWS_POSTS_RESOURCE } from '../../components/pages/news_posts/common';
import {
    retrieveQuestionsDetails,
    formatQuestions,
    formatAnswerSkillData,
    prepareData,
    retrieveAnswersDetails,
    constructVariables,
    formatSections,
} from "../../config/Tools/dataHelpers";
import { format } from "date-fns";
import { stringify } from 'query-string';
import { AdminUserRoles, UserRoles } from '../../constants/user';

const fetchJson = (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({});
    }
    options.headers.set('Accept', 'application/json');
    options.headers.delete('Range');

    options.user = {
        authenticated: true,
        token: getAuthHeaders()['Authorization']
    };
    return fetchUtils.fetchJson(url, options);
};



const customDataProvider = withLifecycleCallbacks(
    simpleRestProvider(`${BASE_URL}${API_ENTRYPOINT}`, fetchJson),
    [
        {
            resource: 'clients',
            afterGetOne: async (params, dataProvider) => {
                const id = params.data.id;
                const response = await dataProvider.getOne('clients', { id });

                const data = prepareData(response.data);

                return { data };
            },
            afterGetList: async (response, params, dataProvider) => {
                const data = response.data.map(json => prepareData(json));
                
                if (document.location.hash === '#/client_statistics') {
                    data.unshift({id: 'all', name: 'Todos'});
                }
                return { data: data, total: response.total };
            },
            beforeCreate: async (params, dataProvider) => handleImageBase64Conversion(params),
            beforeUpdate: async (params, dataProvider) => handleImageBase64Conversion(params),
        },
        {
            resource: 'onboardingconfigs',
            afterGetOne: async (params, dataProvider) => {
                return retrieveQuestionsDetails(params, dataProvider, 'onboardingconfigs');
            },
        },
        {
            resource: 'exitinterviewconfigs',
            afterGetOne: async (params, dataProvider) => {
                return retrieveQuestionsDetails(params, dataProvider, 'exitinterviewconfigs');
            },
        },
        {
            resource: 'suggestionfeedbackconfigs',
            afterGetOne: async (params, dataProvider) => {
                return retrieveQuestionsDetails(params, dataProvider, 'suggestionfeedbackconfigs');
            },
        },
        {
            resource: 'processes',
            afterGetOne: async (params, dataProvider) => {
                return retrieveQuestionsDetails(params, dataProvider, 'processes');
            },
        },
        {
            resource: 'candidatures',
            afterGetOne: async (params, dataProvider) => {
                return retrieveQuestionsDetails(params, dataProvider, 'candidatures');
            },
        },
        {
            resource: 'candidatures',
            beforeCreate: async (params, dataProvider) => {
                const formattedData = formatQuestions(params.data);
                return { ...params, data: formattedData, dataProvider };
            },

            beforeUpdate: async (params, dataProvider) => {
                const formattedData = formatQuestions(params.data);
                return { ...params, data: formattedData, dataProvider };
            },
        },
        {
            resource: 'answers_skills',
            afterGetOne: async (params, dataProvider) => {
                const formattedData = formatAnswerSkillData(params.data);
                return { ...params, data: formattedData, dataProvider }
            },
        },
        {
            resource: 'questions',
            afterGetOne: async (params, dataProvider) => {

                return retrieveAnswersDetails(params, dataProvider, 'questions', 'answers_skills');
            },
            beforeCreate: async (params, dataProvider) => {
                if (params.data.answers) {
                    params.data.answers = params.data.answers.map(answer => ({ id: answer }));
                }else{
                    params.data.answers = [];
                }
                return params;
            },
            beforeUpdate: async (params, dataProvider) => {
                params.data.answers = params.data.answers.map(answer => ({ id: answer }));
                return params;
            },
        },
    ]
);

const veloraProvider = () => {
    const baseUrl = `${BASE_URL}${API_ENTRYPOINT}`;
    const simpleProvider = simpleRestProvider(baseUrl, fetchJson);

    const customProvider = {
        ...simpleProvider,
        getList: async (resource, params) => {
            const { page, perPage } = params.pagination;
            const { field, order } = params.sort;

            const rangeStart = (page - 1) * perPage;
            const rangeEnd = page * perPage - 1;

            const query = {
                sort: JSON.stringify([field, order]),
                range: JSON.stringify([rangeStart, rangeEnd]),
                filter: JSON.stringify(params.filter),
            };
            const url = `${baseUrl}/${resource}?${stringify(query)}`;

            return fetchJson(url).then(({ headers, json }) => {
                return {
                    data: json.data,
                    total: json.total,
                };
            });

        },
    }

    return withLifecycleCallbacks(
        customProvider,
        [
            {
                resource: 'admin_users',
                beforeCreate: async (params, dataProvider) => {
                    params.data.is_active = true;
                    if (params.data.admin_user.role !== AdminUserRoles.ROLE_ADMIN_ROOT) {
                        params.data.technical_selection_user = {
                            role: UserRoles.ATS_ADMIN,
                        };
                    }
                    return params;
                },
                beforeUpdate: async (params, dataProvider) => {
                    params.data.is_active = true;
                    if (params.data?.client?.id) {
                        params.data.client = {
                            id: params.data.client.id,
                        };
                    }
                    return params;
                }
            },
            {
                resource: 'users',
                beforeCreate: async (params, dataProvider) => {
                    params.data.is_active = true;
                    params = await handleImageBase64Conversion(params);
                    params = await formatSections(params);
                    return params;
                },
                beforeUpdate: async (params, dataProvider) =>  {
                    params = await handleImageBase64Conversion(params);
                    params = await formatSections(params);
                    return params;
                },
            },
        ]
    )
};

const geroDataProvider = {
    getList: async (resource, params) => {
        let url;
        let response;
        let data;

        let variablesObject = {};

        if (params.filter.key && params.filter.valor) {
            variablesObject[params.filter.key] = params.filter.valor;
        }

        switch (resource) {
            case 'sessions':
                params = {
                    ...params,
                    filter: {
                        ...params.filter,
                        date_from: params.filter.from,
                        date_to: params.filter.to,
                        chatbots: params.filter.chatbots || [],
                        variables: variablesObject
                    }
                };

                const currentDate = format(new Date(), 'yyyy-MM-dd');
                if (!params.filter.from || params.filter.from === '') {
                    params.filter.from = currentDate;
                }
                if (!params.filter.to || params.filter.to === '') {
                    params.filter.to = currentDate;
                }

                let desiredFilter = {
                    chatbots: params.filter.chatbots,
                    date_from: params.filter.from,
                    date_to: params.filter.to,
                    variables: constructVariables(params.filter.key, params.filter.valor) || {},
                    page: params.pagination.page,
                    limit: params.pagination.perPage
                };

                url = new URL(`${GERO_API_BASE_URL}/stats/sessions`);
                url.searchParams.append('q', JSON.stringify(desiredFilter));

                response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`API response status: ${response.status}`);
                }

                data = await response.json();
                let contentRange = response.headers.get("Content-Range");
                let totalItems = parseInt(contentRange.split("/")[1]);

                return {
                    data,
                    total: totalItems,
                };

            case 'chatbots':
                url = new URL(`${GERO_API_BASE_URL}/stats/chatbots`);
                let chatbotFilter = {};
                if (params.filter) {
                    Object.entries(params.filter).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '') {
                            chatbotFilter[key] = value;
                        }
                    });
                }
                url.searchParams.append('q', JSON.stringify(chatbotFilter));

                response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`API response status: ${response.status}`);
                }

                data = await response.json();
                data.map((e) => {
                    e.name = e.name + " (" + e.id + ")";
                    return e;
                });

                if (params.filter && params.filter.name) {
                    const searchName = params.filter.name.toLowerCase();
                    data = data.filter(chatbot => chatbot.name && chatbot.name.toLowerCase().includes(searchName));
                }

                return {
                    data,
                    total: data.length,
                };

            default:
                throw new Error(`Unsupported resource: ${resource}`);
        }
    },

    getMany: async (resource, params) => {

        let url;
        let response;
        let data;

        switch (resource) {
            case 'sessions':

                break;

            case 'chatbots':
                url = new URL(`${GERO_API_BASE_URL}/stats/chatbots`);

                response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`API response status: ${response.status}`);
                }

                data = await response.json();

                return {
                    data,
                    total: data.length,
                };

            default:
                throw new Error(`Unsupported resource: ${resource}`);
        }
    }
};

const rpaDataProvider = {
    getList: async (resource, params) => {
        let url;
        let data;

        switch (resource) {
            case 'rpa_candidates':

                const currentDate = format(new Date(), 'yyyy-MM-dd');

                const date = params.filter && params.filter.date ? params.filter.date : currentDate;
                const clientIds = params.filter && params.filter.client_id ? params.filter.client_id : [];

                const page = params.pagination.page;
                const perPage = params.pagination.perPage;

                url = new URL(`${BASE_URL}${API_ENTRYPOINT}/babidi/events/rpa_candidates`);
                url.searchParams.append('date', date);
                url.searchParams.append('page', page);
                url.searchParams.append('limit', perPage);

                if (clientIds.length > 0) {
                    clientIds.forEach((clientId) => {
                        url.searchParams.append('clients[]', clientId);
                    });
                }

                try {
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: getAuthHeaders(),
                    });

                    if (!response.ok) {
                        throw new Error(`API response status: ${response.status}`);
                    }

                    data = await response.json();
                    let contentRange = response.headers.get("Content-Range");
                    let totalItems = parseInt(contentRange.split("/")[1]);

                    const dataWithIds = data.map((item, index) => ({
                        ...item,
                        id: index.toString(),
                    }));
                    return {
                        data: dataWithIds,
                        total: totalItems,
                    };
                } catch (error) {

                    throw new Error(`Error fetching data: ${error.message}`);
                }
            default:
                throw new Error(`Unsupported resource: ${resource}`);
        }
    },
};

const v2DataProviderBase = (() => {
    const baseUrl = `${BASE_URL}${API_V2_ENTRYPOINT}`;

    const unwrap = (json) =>
        json?.data !== undefined && !Array.isArray(json.data) ? json.data : json;

    return {
        getList: async (resource, params) => {
            const query = params.filter ? { ...params.filter } : {};
            if (resource === 'customfield' && !query.client_id) {
                return { data: [], total: 0 };
            }
            const url = `${baseUrl}/${resource}?${stringify(query)}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            const data = Array.isArray(json) ? json : (json.data || []);

            if (params.sort?.field) {
                const { field, order } = params.sort;
                data.sort((a, b) => {
                    const aVal = a[field] ?? '';
                    const bVal = b[field] ?? '';
                    if (aVal < bVal) return order === 'ASC' ? -1 : 1;
                    if (aVal > bVal) return order === 'ASC' ? 1 : -1;
                    return 0;
                });
            }

            return { data, total: json.total !== undefined ? json.total : data.length };
        },
        getOne: async (resource, params) => {
            const url = `${baseUrl}/${resource}/${params.id}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            return { data: unwrap(json) };
        },
        getMany: async (resource, params) => {
            const data = await Promise.all(
                params.ids.map(id =>
                    fetch(`${baseUrl}/${resource}/${id}`, { headers: getAuthHeaders() })
                        .then(r => r.json())
                        .then(unwrap)
                )
            );
            return { data };
        },
        getManyReference: async (resource, params) => {
            const query = { [params.target]: params.id, ...params.filter };
            const url = `${baseUrl}/${resource}?${stringify(query)}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            const data = Array.isArray(json) ? json : (json.data || []);
            return { data, total: json.total !== undefined ? json.total : data.length };
        },
        create: async (resource, params) => {
            const url = `${baseUrl}/${resource}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(params.data),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            return { data: unwrap(json) };
        },
        update: async (resource, params) => {
            const url = `${baseUrl}/${resource}/${params.id}`;
            const response = await fetch(url, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(params.data),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            return { data: unwrap(json) };
        },
        updateMany: async (resource, params) => {
            const responses = await Promise.all(
                params.ids.map(id =>
                    fetch(`${baseUrl}/${resource}/${id}`, {
                        method: 'PUT',
                        headers: getAuthHeaders(),
                        body: JSON.stringify(params.data),
                    }).then(r => r.json())
                )
            );
            return { data: responses.map(r => r.id) };
        },
        delete: async (resource, params) => {
            const url = `${baseUrl}/${resource}/${params.id}`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return { data: { id: params.id } };
        },
        deleteMany: async (resource, params) => {
            await Promise.all(
                params.ids.map(id =>
                    fetch(`${baseUrl}/${resource}/${id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                    })
                )
            );
            return { data: params.ids };
        },
    };
})();

const v2DataProvider = withLifecycleCallbacks(v2DataProviderBase, [
    {
        resource: 'customfield',
        afterGetOne: async (params) => {
            // Normalize nested client object → flat client_id for edit form
            if (params.data?.client?.id) {
                params.data.client_id = params.data.client.id;
            }
            return params;
        },
        beforeCreate: async (params) => {
            const { client, options, ...rest } = params.data;
            const data = { ...rest };
            if (client?.id) data.client_id = client.id;
            if (data.type === 'select' && options) data.options = options;
            return { ...params, data };
        },
        beforeUpdate: async (params) => {
            const { id, client, options, ...rest } = params.data;
            const data = { ...rest };
            if (client?.id) data.client_id = client.id;
            if (data.type === 'select' && options) data.options = options;
            return { ...params, data };
        },
    },
]);

/**
 * Bonos de agentes de IA y excedente facturable (86cbaa21u).
 *
 * Provider propio y no `simpleRestProvider` porque estos endpoints no hablan el dialecto de
 * react-admin: esperan `client_id`, `kind`, `from` y `to` como query params sueltos, no un
 * `filter` serializado en JSON. Adaptarlo aqui es preferible a doblar la API del back para
 * encajar en un cliente.
 *
 * **No implementa `update` ni `delete`, y es deliberado**: renovar es insertar un bono nuevo, que
 * entra en cola detras del que este activo, y uno en curso o cerrado ya no se retoca. Si se
 * pudieran editar las cifras o los hitos a mano, la factura dejaria de ser defendible. Rechazan
 * de forma explicita en vez de dejar que react-admin se coma un 404 silencioso.
 */
export const AI_AGENT_PACKS_RESOURCE = 'ai_agent_packs';
export const AI_AGENT_OVERAGE_RESOURCE = 'ai_agent_overage';

const aiAgentProvider = () => {
    const baseUrl = `${BASE_URL}${API_ENTRYPOINT}`;
    const notSupported = (action) => () =>
        Promise.reject(new Error(`Los bonos de agentes de IA no admiten ${action}: renovar es crear uno nuevo.`));

    const getPacks = async (clientId) => {
        const { json } = await fetchJson(`${baseUrl}/ai-agent/packs?${stringify({ client_id: clientId })}`);

        return json?.data ?? [];
    };

    return {
        getList: async (resource, params) => {
            const filter = params?.filter ?? {};

            if (resource === AI_AGENT_OVERAGE_RESOURCE) {
                // El rango de fechas es el nucleo de esta pantalla, no un adorno: sin cliente,
                // tipo y rango no hay nada que preguntar, y preguntar a medias devolveria una
                // cifra que alguien podria facturar.
                if (!filter.client_id || !filter.kind || !filter.from || !filter.to) {
                    return { data: [], total: 0 };
                }

                const query = stringify({
                    client_id: filter.client_id,
                    kind: filter.kind,
                    from: filter.from,
                    to: filter.to,
                });
                const { json } = await fetchJson(`${baseUrl}/ai-agent/overage?${query}`);

                // Una sola fila: react-admin necesita un `id` estable para pintarla.
                return {
                    data: [{ id: `${filter.client_id}|${filter.kind}|${filter.from}|${filter.to}`, ...json }],
                    total: 1,
                };
            }

            if (!filter.client_id) {
                return { data: [], total: 0 };
            }

            const data = await getPacks(filter.client_id);

            return { data, total: data.length };
        },

        getOne: async (resource, params) => {
            // El endpoint no expone el detalle de un bono suelto: se saca del historico del
            // cliente, que es como se consulta siempre.
            const data = await getPacks(params?.meta?.client_id);

            return { data: data.find((pack) => String(pack.id) === String(params.id)) ?? { id: params.id } };
        },

        getMany: async () => ({ data: [] }),
        getManyReference: async () => ({ data: [], total: 0 }),

        create: async (resource, params) => {
            const { json } = await fetchJson(`${baseUrl}/ai-agent/packs`, {
                method: 'POST',
                body: JSON.stringify({
                    client_id: params.data.client_id,
                    kind: params.data.kind,
                    unit: params.data.unit,
                    included_units: params.data.included_units,
                    purchased_at: params.data.purchased_at,
                    notes: params.data.notes || null,
                }),
            });

            return { data: json?.data?.[0] ?? { id: 0 } };
        },

        update: notSupported('edicion'),
        updateMany: notSupported('edicion'),
        delete: notSupported('borrado'),
        deleteMany: notSupported('borrado'),
    };
};

/**
 * Novedades de Velora (86c52b7mu): `ra-data-simple-rest` contra `/api/admin/news-posts`
 * (lista = array + `Content-Range`) más `translate`, que no es CRUD: retraduce los idiomas
 * `auto` y `missing` y devuelve la publicación actualizada. Los 400 de validación se reescriben
 * con `toNewsPostValidationError` para que el aviso y los inputs muestren cada error.
 */
const newsPostsProvider = (() => {
    const baseUrl = `${BASE_URL}${API_ENTRYPOINT}`;
    const simpleProvider = simpleRestProvider(baseUrl, fetchJson);
    const rethrowValidation = (error) => {
        throw toNewsPostValidationError(error);
    };

    return withLifecycleCallbacks(
        {
            ...simpleProvider,
            create: (resource, params) => simpleProvider.create(resource, params).catch(rethrowValidation),
            update: (resource, params) => simpleProvider.update(resource, params).catch(rethrowValidation),
            translate: async (resource, params) => {
                const { json } = await fetchJson(`${baseUrl}/${resource}/${params.id}/translate`, { method: 'POST' })
                    .catch(rethrowValidation);

                return { data: withNewsPostImage(json) };
            },
        },
        [
            {
                resource: NEWS_POSTS_RESOURCE,
                afterRead: async (record) => withNewsPostImage(record),
                afterSave: async (record) => withNewsPostImage(record),
                beforeCreate: async (params) => buildNewsPostPayload(params, { isCreate: true }),
                beforeUpdate: async (params) => buildNewsPostPayload(params, { isCreate: false }),
            },
        ]
    );
})();

const dataProvider = combineDataProviders((resource) => {
    switch (resource) {
        case 'clients':
        case 'filters':
        case 'filter_block':
        case 'candidatures':
        case 'modules':
        case 'skills':
        case 'tags':
        case 'disinteresteds':
        case 'reportfields':
        case 'inboxes':
        case 'default_messages':
        case 'sms_reports/client_list':
        case 'onboardingconfigs':
        case 'questions':
        case 'profiles':
        case 'answers':
        case 'answers_profile':
        case 'answers_skills':
        case 'axis':
        case 'types/mbtis':
        case 'exitinterviewconfigs':
        case 'suggestionfeedbackconfigs':
        case 'availabilities':
        case 'processes':
        case 'processtypes':
        case 'blacklists':
        case 'sms_reports':
            // case 'gero_status':
            return customDataProvider;
        case 'customfield':
            return v2DataProvider;
        case 'chatbots':
        case 'sessions':
            return geroDataProvider;
        case 'rpa_candidates':
            return rpaDataProvider;
        case AI_AGENT_PACKS_RESOURCE:
        case AI_AGENT_OVERAGE_RESOURCE:
            return aiAgentProvider();
        case NEWS_POSTS_RESOURCE:
            return newsPostsProvider;
        case 'users':
        case 'admin_users':
            return veloraProvider();
        default:
            throw new Error(`Unknown resource: ${resource}`);
    }
});

export default dataProvider;
